import React, { useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { startOfMonth, subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PageTitle from '../../common/PageTitle';
import ApiNeededNotice from '../../common/ApiNeededNotice';
import { formatDate } from '../../helpers/UtilityHelper';
import { getMerchant } from '../../helpers/ReduxHelpers';
import {
  pageContainerSx, softCardSubSx, FONT, SIZE, WEIGHT, NAVY, SUBTLE, BORDER, PRIMARY_BLUE, WARNING,
} from '../dashboard/dashboardStyles';
import KpiCard from '../appointments/shared/KpiCard';
import SectionTitle from '../appointments/shared/SectionTitle';
import useAppointmentDashboard from '../appointments/shared/useAppointmentDashboard';
import { exportReport } from '../appointments/shared/exportHelpers';
import { apiConstants, getGeneric } from '../../common/constants';
import usePtlApi from './usePtlApi';
import { EDD_OWNERS, MISS_ATTRIBUTION, MISS_ATTRIBUTION_KEYS, MISS_ATTRIBUTION_SHORT, errorText } from './constants';

const TYPES = [
  { key: 'abd', label: 'ABD' },
  { key: 'nonAbd', label: 'Non-ABD' },
  { key: 'all', label: 'All shipments' },
];
const PERIODS = [
  { key: '7d', label: 'Last 7 days', range: (now) => [subDays(now, 6), now] },
  { key: '30d', label: 'Last 30 days', range: (now) => [subDays(now, 29), now] },
  { key: 'month', label: 'This month', range: (now) => [startOfMonth(now), now] },
];

// ── ABD buckets: one /appointment_delivery/dashboard call, same baseline (merchant, ABD, AWB date range)
const DELIVERED = { field: 'orderStatus', operator: 'eq', value: 'DELIVERED' };
const RTO = { field: 'orderStatus', operator: 'like', value: 'RTO.*' };
const CLOSED_OTHER = { field: 'orderStatus', operator: 'in', value: ['CANCELLED_ORDER', 'LOST', 'DAMAGED'] };
const NEVER_MISSED = { field: 'appointmentDeliveryHistory.0', operator: 'exists', value: false };
const latestMiss = (category) => ({ field: 'appointmentDeliveryHistory.closureCategory', operator: 'lasteq', value: category });

const MISS_ROWS = [
  { key: 'm1', label: '1 miss', filter: { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 1 } },
  { key: 'm2', label: '2 misses', filter: { field: 'appointmentDeliveryHistoryCount', operator: 'eq', value: 2 } },
  { key: 'm3', label: '3 or more misses', filter: { field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 3 } },
];

const ABD_BUCKETS = [
  { name: 'all', andfilter: [] },
  { name: 'delivered', andfilter: [DELIVERED] },
  { name: 'firstAttempt', andfilter: [DELIVERED, NEVER_MISSED] },
  { name: 'missed', andfilter: [{ field: 'appointmentDeliveryHistoryCount', operator: 'gte', value: 1 }] },
  ...MISS_ROWS.flatMap((r) => [
    { name: `${r.key}_all`, andfilter: [r.filter] },
    { name: `${r.key}_delivered`, andfilter: [r.filter, DELIVERED] },
    { name: `${r.key}_rto`, andfilter: [r.filter, RTO] },
    { name: `${r.key}_closed`, andfilter: [r.filter, CLOSED_OTHER] },
    ...MISS_ATTRIBUTION_KEYS.map((c) => ({ name: `${r.key}_${c}`, andfilter: [r.filter, latestMiss(c)] })),
  ]),
];
// The dashboard API takes at most 20 buckets per call, so the 28 go in two calls.
const MAX_BUCKETS_PER_CALL = 20;
const ABD_BUCKETS_A = ABD_BUCKETS.slice(0, MAX_BUCKETS_PER_CALL);
const ABD_BUCKETS_B = ABD_BUCKETS.slice(MAX_BUCKETS_PER_CALL);

const num = (n) => (n == null ? '—' : Number(n).toLocaleString('en-IN'));
const pct = (part, whole) => (whole ? Math.round((1000 * part) / whole) / 10 : 0);

/** One row per miss count: shipments, what happened to them, and who the latest miss is on. */
function missTableRows(b) {
  return MISS_ROWS.map((r) => {
    const all = b[`${r.key}_all`] || 0;
    const delivered = b[`${r.key}_delivered`] || 0;
    const rto = b[`${r.key}_rto`] || 0;
    const closed = b[`${r.key}_closed`] || 0;
    const owners = MISS_ATTRIBUTION_KEYS.map((c) => b[`${r.key}_${c}`] || 0);
    return {
      key: r.key, label: r.label, all, delivered, rto, closed,
      open: Math.max(0, all - delivered - rto - closed),
      owners,
      notRecorded: Math.max(0, all - owners.reduce((s, x) => s + x, 0)),
    };
  });
}

// Filter card in the same style as PoD Analytics (Admin Panel filter bar): small labels, compact toggle groups.
const filterLabelSx = { fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5, whiteSpace: 'nowrap' };
const toggleSx = { textTransform: 'none', px: 1.25, height: 33, whiteSpace: 'nowrap', fontSize: 13 };
const headSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold, color: SUBTLE, whiteSpace: 'nowrap', borderColor: BORDER };
const cellSx = { fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, fontVariantNumeric: 'tabular-nums', borderColor: BORDER };

function Segmented({ options, value, onChange, label }) {
  return (
    <ToggleButtonGroup size='small' exclusive value={value} aria-label={label} onChange={(_e, v) => v && onChange(v)}>
      {options.map((o) => <ToggleButton key={o.key} value={o.key} sx={toggleSx}>{o.label}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

/** "12 (34%)" cell: count with its share of the row. */
// ── "After each missed appointment" table: two column groups, each with its own tint and a divider before it
const OUTCOME_TINT = `${PRIMARY_BLUE}0D`;
const OWNER_TINT = `${WARNING}12`;
const dividerSx = { borderLeft: `2px solid ${BORDER}` };
const OUTCOME_COLS = [
  { key: 'delivered', label: 'Delivered', info: 'Delivered to the consignee in the end' },
  { key: 'rto', label: 'RTO', info: 'Returned to origin' },
  { key: 'open', label: 'Still open', info: 'Not delivered yet, still in progress' },
  { key: 'closed', label: 'Cancelled / lost', info: 'Cancelled, lost or damaged' },
];
const OWNER_COLS = [
  ...MISS_ATTRIBUTION_KEYS.map((c) => ({ key: c, label: MISS_ATTRIBUTION_SHORT[c], info: MISS_ATTRIBUTION[c].label, color: MISS_ATTRIBUTION[c].color })),
  { key: 'notRecorded', label: 'Not recorded', info: 'No attribution recorded for the latest miss yet', color: SUBTLE },
];

/** Sum of the miss rows, shown as the last row. */
const totalRow = (rows) => rows.reduce((t, r) => ({
  ...t,
  all: t.all + r.all, delivered: t.delivered + r.delivered, rto: t.rto + r.rto, open: t.open + r.open, closed: t.closed + r.closed,
  owners: t.owners.map((v, i) => v + r.owners[i]), notRecorded: t.notRecorded + r.notRecorded,
}), { key: 'total', label: 'All missed', all: 0, delivered: 0, rto: 0, open: 0, closed: 0, owners: MISS_ATTRIBUTION_KEYS.map(() => 0), notRecorded: 0 });

function GroupHead({ colSpan, title, hint, tint, divider }) {
  return (
    <TableCell colSpan={colSpan} align='center' sx={{ ...headSx, bgcolor: tint, py: 1, ...(divider ? dividerSx : {}) }}>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY }}>{title}</Typography>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE, whiteSpace: 'normal' }}>{hint}</Typography>
    </TableCell>
  );
}

function ColHead({ label, info, dot, tint, divider, align = 'right' }) {
  return (
    <TableCell align={align} sx={{ ...headSx, bgcolor: tint, ...(divider ? dividerSx : {}) }}>
      <Tooltip title={info} arrow>
        <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
          {dot && <Box component='span' sx={{ width: 7, height: 7, borderRadius: '2px', bgcolor: dot }} />}
          {label}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

/** Count on top, its share of the row underneath. */
function CountCell({ value, of, divider }) {
  return (
    <TableCell align='right' sx={{ ...cellSx, ...(divider ? dividerSx : {}) }}>
      <Box sx={{ fontWeight: WEIGHT.semibold }}>{num(value)}</Box>
      {of ? <Box sx={{ fontSize: SIZE['3xs'], color: SUBTLE }}>{pct(value, of)}%</Box> : null}
    </TableCell>
  );
}

// ── Client-attributable reasons (ABD: reason of each missed appointment; Non-ABD: delay reason of the EDD breach)
const ABD_CLIENT_CATEGORY = 'NON_PROZO_ATTRIBUTABLE';

/** ABD reasons from the dashboard `reasons` block: client-attributable only, most AWBs first. */
function abdClientReasons(reasons = []) {
  return reasons
    .filter((r) => r.category === ABD_CLIENT_CATEGORY)
    .map((r) => ({ key: r.code, label: r.label || r.code, awbs: r.awbCount ?? r.count ?? 0, misses: r.count || 0 }))
    .sort((a, b) => b.awbs - a.awbs);
}

/** Non-ABD reasons from the performance response; null when the backend does not send them yet. */
function nonAbdClientReasons(d = {}) {
  if (!Array.isArray(d.clientReasons)) return null;
  return d.clientReasons.map((r) => ({
    key: r.code || 'none', label: r.label || 'Reason not recorded', awbs: r.awbCount || 0, late: r.deliveredLate || 0, open: r.notDelivered || 0,
  }));
}

function ClientReasonsCard({ title, hint, rows, loading, columns, footnote }) {
  const total = (rows || []).reduce((sum, r) => sum + r.awbs, 0);
  return (
    <Card variant='outlined' sx={{ p: 2, height: '100%' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ ...softCardSubSx, mt: 0, mb: 1.25 }}>{hint}</Typography>
      {!loading && rows === null ? (
        <Alert severity='info'>Shows here once the backend update is deployed.</Alert>
      ) : (
        <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
          <Table size='small' sx={{ minWidth: 420 }}>
            <TableHead>
              <TableRow>
                <ColHead label='Attribution reason' info='Client-attributable reason Prozo recorded' align='left' />
                <ColHead label='AWBs' info='Shipments with this reason' />
                {columns.map((c) => <ColHead key={c.key} label={c.label} info={c.info} />)}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? [0, 1, 2].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 2 + columns.length }).map((_, j) => (
                    <TableCell key={j} sx={cellSx} align={j ? 'right' : 'left'}><Skeleton variant='text' width={j ? 40 : 160} sx={j ? { ml: 'auto' } : undefined} /></TableCell>
                  ))}
                </TableRow>
              )) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2 + columns.length} sx={{ ...cellSx, color: SUBTLE, textAlign: 'center', py: 3 }}>
                    No client-attributable reasons recorded in this period.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => (
                <TableRow key={r.key}>
                  <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.semibold }}>{r.label}</TableCell>
                  <CountCell value={r.awbs} of={total} />
                  {columns.map((c) => <TableCell key={c.key} sx={cellSx} align='right'>{num(r[c.key])}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {footnote && <Typography sx={{ ...softCardSubSx, mt: 1 }}>{footnote}</Typography>}
    </Card>
  );
}

const ABD_REASON_COLS = [{ key: 'misses', label: 'Missed appointments', info: 'Missed appointments with this reason (an AWB can miss more than once)' }];
const NON_ABD_REASON_COLS = [
  { key: 'late', label: 'Delivered after EDD', info: 'Delivered on a later day than the EDD' },
  { key: 'open', label: 'Not delivered', info: 'Not delivered yet, EDD day passed' },
];

function AbdClientReasons({ dash, period }) {
  return (
    <ClientReasonsCard title='Client-attributable reasons · ABD' columns={ABD_REASON_COLS} loading={dash.loading}
      rows={abdClientReasons(dash.data.reasons)}
      hint={`Reasons recorded against the client for missed appointments, AWB registered in ${period.label.toLowerCase()}. Each % is of the AWBs in this table.`}
      footnote='An AWB that missed appointments for different reasons is counted under each of them.' />
  );
}

function NonAbdClientReasons({ state, period }) {
  return (
    <ClientReasonsCard title='Client-attributable reasons · Non-ABD' columns={NON_ABD_REASON_COLS} loading={state.loading}
      rows={nonAbdClientReasons(state.data || {})}
      hint={`EDD breaches Prozo attributed to the client, AWB registered in ${period.label.toLowerCase()}. Each % is of the AWBs in this table.`} />
  );
}

function AbdSection({ dash, period }) {
  const b = dash.data.buckets || {};
  const loading = dash.loading;
  const total = b.all || 0;
  const rows = missTableRows(b);
  const val = (v) => (loading ? undefined : v ?? 0);

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle title='ABD performance' hint={`ABD shipments with the AWB registered in ${period.label.toLowerCase()}`} />
      {dash.error ? (
        <Alert severity='error'>{errorText(dash.error, 'Could not load the ABD performance. Please try again.')}</Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='blue' label='ABD Shipments' loading={loading} value={val(total)} sub='AWB registered in the period' icon={<LocalShippingOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='green' label='Delivered' loading={loading} value={val(b.delivered)} sub={`${pct(b.delivered, total)}% of shipments`} icon={<TaskAltIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='teal' label='Delivered on 1st Attempt' loading={loading} value={val(b.firstAttempt)}
                sub={`${pct(b.firstAttempt, total)}% of shipments · ${pct(b.firstAttempt, b.delivered)}% of delivered`} icon={<EmojiEventsOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='amber' label='Missed at Least Once' loading={loading} value={val(b.missed)} sub={`${pct(b.missed, total)}% of shipments`} icon={<EventBusyOutlinedIcon />} />
            </Grid>
          </Grid>

          <Card variant='outlined' sx={{ p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.5 }}>After each missed appointment</Typography>
            <Typography sx={{ ...softCardSubSx, mt: 0, mb: 1.25 }}>
              Shipments grouped by how many appointments they missed. Each % is of the shipments in that row.
            </Typography>
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 1040 }}>
                <TableHead>
                  <TableRow>
                    <GroupHead colSpan={2} title='Shipments' hint='Missed at least once' />
                    <GroupHead colSpan={OUTCOME_COLS.length} title='What happened to them' hint='Status of the shipment today' tint={OUTCOME_TINT} divider />
                    <GroupHead colSpan={OWNER_COLS.length} title='Who the last miss was on' hint='Attribution Prozo recorded for the latest miss' tint={OWNER_TINT} divider />
                  </TableRow>
                  <TableRow>
                    <ColHead label='Misses' info='How many appointments the shipment missed' align='left' />
                    <ColHead label='Shipments' info='Shipments in this row' />
                    {OUTCOME_COLS.map((c, i) => <ColHead key={c.key} label={c.label} info={c.info} tint={OUTCOME_TINT} divider={i === 0} />)}
                    {OWNER_COLS.map((c, i) => <ColHead key={c.key} label={c.label} info={c.info} dot={c.color} tint={OWNER_TINT} divider={i === 0} />)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...rows, totalRow(rows)].map((r) => {
                    const isTotal = r.key === 'total';
                    const rowSx = isTotal ? { bgcolor: '#f8fafc', '& td': { fontWeight: WEIGHT.bold, borderBottom: 0 } } : undefined;
                    return (
                      <TableRow key={r.key} sx={rowSx}>
                        <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.semibold, whiteSpace: 'nowrap' }}>{r.label}</TableCell>
                        {loading ? (
                          Array.from({ length: 1 + OUTCOME_COLS.length + OWNER_COLS.length }).map((_, i) => (
                            <TableCell key={i} sx={{ ...cellSx, ...(i === 1 || i === 1 + OUTCOME_COLS.length ? dividerSx : {}) }} align='right'>
                              <Skeleton variant='text' width={40} sx={{ ml: 'auto' }} />
                            </TableCell>
                          ))
                        ) : (
                          <>
                            <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.bold }} align='right'>{num(r.all)}</TableCell>
                            {OUTCOME_COLS.map((c, i) => <CountCell key={c.key} value={r[c.key]} of={r.all} divider={i === 0} />)}
                            {OWNER_COLS.map((c, i) => <CountCell key={c.key} value={c.key === 'notRecorded' ? r.notRecorded : r.owners[i]} of={r.all} divider={i === 0} />)}
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <Box sx={{ mt: 2.5 }}><AbdClientReasons dash={dash} period={period} /></Box>
        </>
      )}
    </Box>
  );
}

// ── Non-ABD: GET merchant/ptl/edd-breach/performance (late = delivered on a later day than the EDD day)
const NON_ABD_OWNER_COLS = [
  { key: 'PROZO', label: 'Prozo', info: EDD_OWNERS.PROZO.hint, color: EDD_OWNERS.PROZO.color },
  { key: 'CLIENT', label: 'Client', info: EDD_OWNERS.CLIENT.hint, color: EDD_OWNERS.CLIENT.color },
  { key: 'EXTERNAL', label: 'Force majeure', info: EDD_OWNERS.EXTERNAL.hint, color: EDD_OWNERS.EXTERNAL.color },
  { key: 'NONE', label: 'Not attributed', info: 'Prozo has not recorded who the delay was on yet', color: SUBTLE },
];
// Late means at least one day after the EDD, so the first bucket starts at 1 day.
const LATE_BUCKET_LABELS = { '0-2d': '1–2 days late', '3-5d': '3–5 days late', '6-10d': '6–10 days late', '10d+': 'More than 10 days late', All: 'All late' };

function NonAbdSection({ state, period }) {
  const d = state.data || {};
  const loading = state.loading;
  const val = (v) => (loading ? undefined : v ?? 0);
  const measured = (d.deliveredOnTime || 0) + (d.deliveredLate || 0);
  const matrix = d.lateMatrix || [];

  if (state.missing) {
    return (
      <Box sx={{ mb: 2.5 }}>
        <ApiNeededNotice title='Non-ABD performance' status='Backend not deployed yet'
          summary='Delivered on time vs after EDD, with delay bands and attribution, for the selected period.'
          endpoint='GET /api/merchant/ptl/edd-breach/performance?from&to'
          footnote='Shows here once this API is deployed. The Non-ABD shipment rows are already in the report download.' />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle title='Non-ABD performance' hint={`Non-ABD shipments with the AWB registered in ${period.label.toLowerCase()}, delivered against their EDD`} />
      {state.error ? (
        <Alert severity='error'>{errorText(state.error, 'Could not load the Non-ABD performance. Please try again.')}</Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='blue' label='Non-ABD Shipments' loading={loading} value={val(d.shipments)}
                sub={`${num(d.delivered || 0)} delivered`} icon={<LocalShippingOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='green' label='Delivered on Time' loading={loading} value={val(d.deliveredOnTime)}
                sub={`${pct(d.deliveredOnTime, measured)}% of delivered · by the EDD day`} icon={<TaskAltIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='red' label='Delivered after EDD' loading={loading} value={val(d.deliveredLate)}
                sub={`${pct(d.deliveredLate, measured)}% of delivered`} icon={<EventBusyOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='amber' label='Not Delivered, Past EDD' loading={loading} value={val(d.openPastEdd)}
                sub={`Of ${num(d.open || 0)} still in transit`} icon={<AccessTimeIcon />} />
            </Grid>
          </Grid>

          <Card variant='outlined' sx={{ p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.5 }}>Delivered after EDD</Typography>
            <Typography sx={{ ...softCardSubSx, mt: 0, mb: 1.25 }}>
              Late deliveries by how many days late they were. Each % is of the shipments in that row.
            </Typography>
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <GroupHead colSpan={2} title='Late deliveries' hint='Delivered on a later day than the EDD' />
                    <GroupHead colSpan={NON_ABD_OWNER_COLS.length} title='Who the delay was on' hint='Attribution Prozo recorded for the delay' tint={OWNER_TINT} divider />
                  </TableRow>
                  <TableRow>
                    <ColHead label='Days late' info='Days between the EDD and the delivery' align='left' />
                    <ColHead label='Shipments' info='Late deliveries in this row' />
                    {NON_ABD_OWNER_COLS.map((c, i) => <ColHead key={c.key} label={c.label} info={c.info} dot={c.color} tint={OWNER_TINT} divider={i === 0} />)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(loading ? Object.keys(LATE_BUCKET_LABELS).map((bucket) => ({ bucket })) : matrix).map((r) => {
                    const isTotal = r.bucket === 'All';
                    return (
                      <TableRow key={r.bucket} sx={isTotal ? { bgcolor: '#f8fafc', '& td': { fontWeight: WEIGHT.bold, borderBottom: 0 } } : undefined}>
                        <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.semibold, whiteSpace: 'nowrap' }}>{LATE_BUCKET_LABELS[r.bucket] || r.bucket}</TableCell>
                        {loading ? (
                          Array.from({ length: 1 + NON_ABD_OWNER_COLS.length }).map((_, i) => (
                            <TableCell key={i} sx={{ ...cellSx, ...(i === 1 ? dividerSx : {}) }} align='right'><Skeleton variant='text' width={40} sx={{ ml: 'auto' }} /></TableCell>
                          ))
                        ) : (
                          <>
                            <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.bold }} align='right'>{num(r.total)}</TableCell>
                            {NON_ABD_OWNER_COLS.map((c, i) => <CountCell key={c.key} value={r.counts?.[c.key] || 0} of={r.total} divider={i === 0} />)}
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ ...softCardSubSx, mt: 1 }}>
              Joint attribution is not recorded for Non-ABD shipments.
              {d.deliveredNotMeasured ? ` ${num(d.deliveredNotMeasured)} delivered shipment(s) have no EDD and are not counted as on time or late.` : ''}
            </Typography>
          </Card>
          <Box sx={{ mt: 2.5 }}><NonAbdClientReasons state={state} period={period} /></Box>
        </>
      )}
    </Box>
  );
}

// ── All shipments: one cumulative view of ABD + Non-ABD (not the two sections one after the other)
// On time = ABD delivered on the first appointment + Non-ABD delivered by the EDD day.
// Delayed = ABD delivered after a missed appointment + Non-ABD delivered after the EDD day.
// Delays by attribution = ABD shipments that missed an appointment (latest miss) + Non-ABD late deliveries.
const ALL_OWNER_ROWS = [
  { key: 'prozo', label: 'Prozo attributable', color: MISS_ATTRIBUTION.PROZO_ATTRIBUTABLE.color, abd: 'PROZO_ATTRIBUTABLE', nonAbd: 'PROZO' },
  { key: 'client', label: 'Client attributable', color: MISS_ATTRIBUTION.NON_PROZO_ATTRIBUTABLE.color, abd: 'NON_PROZO_ATTRIBUTABLE', nonAbd: 'CLIENT' },
  { key: 'joint', label: 'Joint', color: MISS_ATTRIBUTION.JOINT_ATTRIBUTABLE.color, abd: 'JOINT_ATTRIBUTABLE', nonAbd: null },
  { key: 'fm', label: 'Force majeure', color: MISS_ATTRIBUTION.FORCE_MAJEURE.color, abd: 'FORCE_MAJEURE', nonAbd: 'EXTERNAL' },
  { key: 'none', label: 'Not attributed', color: SUBTLE, abd: null, nonAbd: 'NONE' },
];

/** Cumulative numbers from the ABD buckets (`b`) and the Non-ABD performance response (`n`). */
function cumulative(b = {}, n = {}) {
  const abdDelivered = b.delivered || 0;
  const abdOnTime = b.firstAttempt || 0;
  const shipments = (b.all || 0) + (n.shipments || 0);
  const delivered = abdDelivered + (n.delivered || 0);
  const onTime = abdOnTime + (n.deliveredOnTime || 0);
  const delayed = Math.max(0, abdDelivered - abdOnTime) + (n.deliveredLate || 0);
  const missRows = missTableRows(b);
  const abdOwner = (c) => (c ? missRows.reduce((sum, r) => sum + (r.owners[MISS_ATTRIBUTION_KEYS.indexOf(c)] || 0), 0)
    : missRows.reduce((sum, r) => sum + r.notRecorded, 0));
  const nonAbdAll = (n.lateMatrix || []).find((r) => r.bucket === 'All')?.counts || {};
  const owners = ALL_OWNER_ROWS.map((o) => ({ ...o, value: abdOwner(o.abd) + (o.nonAbd ? nonAbdAll[o.nonAbd] || 0 : 0) }));
  return { shipments, delivered, onTime, delayed, measured: onTime + delayed, owners, delays: owners.reduce((sum, o) => sum + o.value, 0) };
}

function AllSection({ dash, nonAbd, period }) {
  const loading = dash.loading || nonAbd.loading;
  const error = dash.error || nonAbd.error;
  const c = cumulative(dash.data.buckets || {}, nonAbd.data || {});
  const val = (v) => (loading ? undefined : v ?? 0);

  return (
    <Box sx={{ mb: 3 }}>
      <SectionTitle title='All shipments performance' hint={`ABD and Non-ABD together · AWB registered in ${period.label.toLowerCase()}`} />
      {error ? (
        <Alert severity='error'>{errorText(error, 'Could not load the performance. Please try again.')}</Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2.5 }}>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='blue' label='PTL Shipments' loading={loading} value={val(c.shipments)} sub='ABD + Non-ABD' icon={<LocalShippingOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='green' label='Delivered' loading={loading} value={val(c.delivered)} sub={`${pct(c.delivered, c.shipments)}% of shipments`} icon={<TaskAltIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='teal' label='Delivered on Time' loading={loading} value={val(c.onTime)} sub={`${pct(c.onTime, c.measured)}% of delivered`}
                info='ABD: delivered on the first appointment. Non-ABD: delivered by the EDD day.' icon={<EmojiEventsOutlinedIcon />} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard color='red' label='Delivered with a Delay' loading={loading} value={val(c.delayed)} sub={`${pct(c.delayed, c.measured)}% of delivered`}
                info='ABD: delivered after a missed appointment. Non-ABD: delivered after the EDD day.' icon={<EventBusyOutlinedIcon />} />
            </Grid>
          </Grid>

          <Card variant='outlined' sx={{ p: 2 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.5 }}>Delays by attribution</Typography>
            <Typography sx={{ ...softCardSubSx, mt: 0, mb: 1.25 }}>
              ABD shipments that missed an appointment (who the latest miss is on) and Non-ABD shipments delivered after EDD, together.
            </Typography>
            <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '12px', overflowX: 'auto' }}>
              <Table size='small' sx={{ minWidth: 480 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headSx}>Attribution</TableCell>
                    <TableCell sx={headSx} align='right'>Delays</TableCell>
                    <TableCell sx={headSx} align='right'>% of delays</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...c.owners, { key: 'total', label: 'All delays', value: c.delays }].map((o) => (
                    <TableRow key={o.key} sx={o.key === 'total' ? { bgcolor: '#f8fafc', '& td': { fontWeight: WEIGHT.bold, borderBottom: 0 } } : undefined}>
                      <TableCell sx={{ ...cellSx, fontWeight: WEIGHT.semibold }}>
                        {o.color && <Box component='span' sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', bgcolor: o.color, mr: 1 }} />}
                        {o.label}
                      </TableCell>
                      <TableCell sx={cellSx} align='right'>{loading ? <Skeleton variant='text' width={40} sx={{ ml: 'auto' }} /> : num(o.value)}</TableCell>
                      <TableCell sx={cellSx} align='right'>{loading ? <Skeleton variant='text' width={40} sx={{ ml: 'auto' }} /> : `${pct(o.value, c.delays)}%`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography sx={{ ...softCardSubSx, mt: 1 }}>Joint is recorded for ABD shipments only.</Typography>
          </Card>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} lg={6}><AbdClientReasons dash={dash} period={period} /></Grid>
            <Grid item xs={12} lg={6}><NonAbdClientReasons state={nonAbd} period={period} /></Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

/** Merchant PTL performance for a period: ABD first-attempt delivery and outcome after each miss; Non-ABD delivery against EDD. */
export default function PerformanceOverview() {
  const { enqueueSnackbar } = useSnackbar();
  const [type, setType] = useState('abd');
  const [periodKey, setPeriodKey] = useState('30d');
  const [downloading, setDownloading] = useState(false);
  const period = PERIODS.find((p) => p.key === periodKey);
  const [fromDate, toDate] = useMemo(() => period.range(new Date()), [period]);
  const filters = useMemo(() => ({ fromDate, toDate }), [fromDate, toDate]);
  const showAbd = type !== 'nonAbd';
  const showNonAbd = type !== 'abd';

  const dashA = useAppointmentDashboard({ filters, include: ['buckets', 'reasons'], buckets: ABD_BUCKETS_A });
  const dashB = useAppointmentDashboard({ filters, include: ['buckets'], buckets: ABD_BUCKETS_B });
  const merchantOid = getMerchant()?.id || '';
  const nonAbdParams = { from: formatDate(fromDate, 'yyyy-MM-dd'), to: formatDate(toDate, 'yyyy-MM-dd'), ...(merchantOid ? { merchantOid } : {}) };
  const nonAbd = usePtlApi(() => getGeneric(`${apiConstants.PTL_EDD_BREACH_MERCHANT}/performance`, nonAbdParams),
    JSON.stringify(nonAbdParams), { skip: !showNonAbd });
  const dash = {
    loading: dashA.loading || dashB.loading,
    error: dashA.error || dashB.error,
    data: { buckets: { ...dashA.data.buckets, ...dashB.data.buckets }, reasons: dashA.data.reasons || [] },
  };

  const download = async () => {
    setDownloading(true);
    try {
      const b = dash.data.buckets || {};
      const range = `${formatDate(fromDate, 'dd MMM yyyy')} to ${formatDate(toDate, 'dd MMM yyyy')}`;
      const sheets = [{
        name: 'Filters',
        rows: [
          { Filter: 'Merchant', Value: getMerchant()?.name || '' },
          { Filter: 'Shipments', Value: TYPES.find((t) => t.key === type).label },
          { Filter: 'Period', Value: `${period.label} (${range})` },
          { Filter: 'Date basis', Value: 'AWB registered date' },
          { Filter: 'Generated at', Value: formatDate(new Date(), 'dd MMM yyyy, h:mm a') },
        ],
      }];
      const shipments = [];
      if (type === 'all') {
        const c = cumulative(b, nonAbd.data || {});
        sheets.push({
          name: 'All summary',
          rows: [
            { Metric: 'PTL shipments (ABD + Non-ABD)', Count: c.shipments },
            { Metric: 'Delivered', Count: c.delivered, '%': pct(c.delivered, c.shipments) },
            { Metric: 'Delivered on time (ABD 1st appointment, Non-ABD by EDD)', Count: c.onTime, '%': pct(c.onTime, c.measured) },
            { Metric: 'Delivered with a delay (ABD after a miss, Non-ABD after EDD)', Count: c.delayed, '%': pct(c.delayed, c.measured) },
          ],
        });
        sheets.push({
          name: 'All delays by attribution',
          rows: [...c.owners.map((o) => ({ Attribution: o.label, Delays: o.value, '% of delays': pct(o.value, c.delays) })),
            { Attribution: 'All delays', Delays: c.delays, '% of delays': 100 }],
        });
      }
      if (showAbd && type !== 'all') {
        const total = b.all || 0;
        sheets.push({
          name: 'ABD summary',
          rows: [
            { Metric: 'ABD shipments', Count: total, '% of shipments': 100 },
            { Metric: 'Delivered', Count: b.delivered || 0, '% of shipments': pct(b.delivered, total) },
            { Metric: 'Delivered on 1st attempt', Count: b.firstAttempt || 0, '% of shipments': pct(b.firstAttempt, total) },
            { Metric: 'Missed at least once', Count: b.missed || 0, '% of shipments': pct(b.missed, total) },
          ],
        });
        sheets.push({
          name: 'ABD after each miss',
          rows: missTableRows(b).map((r) => ({
            Misses: r.label, Shipments: r.all, Delivered: r.delivered, RTO: r.rto, 'Still open': r.open, 'Cancelled / lost': r.closed,
            ...MISS_ATTRIBUTION_KEYS.reduce((acc, c, i) => ({ ...acc, [MISS_ATTRIBUTION_SHORT[c]]: r.owners[i] }), {}),
            'Not recorded': r.notRecorded,
          })),
        });
        shipments.push({ name: 'ABD shipments', filters, appointmentRequired: true });
      }
      if (showNonAbd) {
        const n = nonAbd.data;
        if (n && type !== 'all') {
          const measured = (n.deliveredOnTime || 0) + (n.deliveredLate || 0);
          sheets.push({
            name: 'Non-ABD summary',
            rows: [
              { Metric: 'Non-ABD shipments', Count: n.shipments || 0 },
              { Metric: 'Delivered', Count: n.delivered || 0 },
              { Metric: 'Delivered on time (by the EDD day)', Count: n.deliveredOnTime || 0, '% of delivered': pct(n.deliveredOnTime, measured) },
              { Metric: 'Delivered after EDD', Count: n.deliveredLate || 0, '% of delivered': pct(n.deliveredLate, measured) },
              { Metric: 'Delivered, no EDD (not measured)', Count: n.deliveredNotMeasured || 0 },
              { Metric: 'Not delivered, past EDD', Count: n.openPastEdd || 0 },
              { Metric: 'Still in transit', Count: n.open || 0 },
              { Metric: 'RTO / cancelled / lost', Count: n.closedWithoutDelivery || 0 },
            ],
          });
          sheets.push({
            name: 'Non-ABD late by delay',
            rows: (n.lateMatrix || []).map((r) => ({
              'Days late': LATE_BUCKET_LABELS[r.bucket] || r.bucket, Shipments: r.total,
              ...NON_ABD_OWNER_COLS.reduce((acc, c) => ({ ...acc, [c.label]: r.counts?.[c.key] || 0 }), {}),
            })),
          });
        }
        shipments.push({ name: 'Non-ABD shipments', filters, appointmentRequired: false });
      }
      if (showAbd) {
        sheets.push({
          name: 'ABD client reasons',
          rows: abdClientReasons(dash.data.reasons).map((r) => ({ 'Attribution reason': r.label, AWBs: r.awbs, 'Missed appointments': r.misses })),
        });
      }
      const nonAbdReasons = showNonAbd ? nonAbdClientReasons(nonAbd.data || {}) : null;
      if (nonAbdReasons) {
        sheets.push({
          name: 'Non-ABD client reasons',
          rows: nonAbdReasons.map((r) => ({ 'Attribution reason': r.label, AWBs: r.awbs, 'Delivered after EDD': r.late, 'Not delivered': r.open })),
        });
      }
      await exportReport({ fileName: 'PTL_Performance_Overview', sheets, shipments });
      enqueueSnackbar('Report ready', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Download failed. Please try again.'), { variant: 'error' });
    }
    setDownloading(false);
  };

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Delivery performance of your PTL shipments over a period'>Performance Overview</PageTitle>

      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', py: '12px !important' }}>
          <Box>
            <Typography sx={filterLabelSx}>Shipments</Typography>
            <Segmented label='Shipments' options={TYPES} value={type} onChange={setType} />
          </Box>
          <Box>
            <Typography sx={filterLabelSx}>
              Period · {formatDate(fromDate, 'dd MMM')} – {formatDate(toDate, 'dd MMM yyyy')}
              <Box component='span' sx={{ fontWeight: 400 }}> (AWB registered date)</Box>
            </Typography>
            <Segmented label='Period' options={PERIODS} value={periodKey} onChange={setPeriodKey} />
          </Box>
          <LoadingButton size='small' variant='contained' loading={downloading} disabled={(showAbd && dash.loading) || (showNonAbd && nonAbd.loading)} onClick={download}
            startIcon={<FileDownloadOutlinedIcon />} sx={{ ml: 'auto', height: 33, textTransform: 'none', whiteSpace: 'nowrap' }}>
            Download report
          </LoadingButton>
        </CardContent>
      </Card>

      {type === 'all' ? <AllSection dash={dash} nonAbd={nonAbd} period={period} /> : (
        <>
          {showAbd && <AbdSection dash={dash} period={period} />}
          {showNonAbd && <NonAbdSection state={nonAbd} period={period} />}
        </>
      )}
    </Box>
  );
}
